import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, AlertCircle, Search, Trash2, Printer, Eye, ArrowRight, RotateCcw, BookOpen, Zap, RefreshCw } from 'lucide-react';
import { getCOA, getJurnal, createJurnal, deleteJurnal, createJurnalDariTagihan, getPenerimaanPembelianById, getPurchaseOrderById, getKandidatJurnalOtomatis, createJurnalDariPembelian, createJurnalDariPenerimaan, createJurnalDariGaji } from '../../api';
import { useAuth } from '../../AuthContext';
import { sumberInfo, badgeSumber } from '../../utils/sumberJurnal';

// ===== Searchable Akun Select — ketik untuk mencari (kode / nama akun) =====
function SearchableAkunSelect({ coaList, value, onSelect, placeholder }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selected = coaList.find(c => c.id === Number(value));

  const filtered = coaList.filter(c => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (c.kode_akun || '').toLowerCase().includes(q) ||
           (c.nama_akun || '').toLowerCase().includes(q);
  }).slice(0, 200);

  // Tutup dropdown saat klik di luar
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const display = selected ? `${selected.kode_akun} - ${selected.nama_akun}` : '';

  return (
    <div ref={ref} style={{ position: 'relative', flex: 2 }}>
      <input
        type="text"
        value={open ? query : display}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setQuery(''); setOpen(true); }}
        placeholder={placeholder || 'Ketik kode / nama akun...'}
        autoComplete="off"
        style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--gray-200)', borderRadius: '6px', fontSize: '0.82rem', background: '#fff', boxSizing: 'border-box', fontWeight: selected ? 600 : 400 }}
      />
      {selected && !open && (
        <div style={{ position: 'absolute', right: 8, top: 7, fontSize: '0.65rem', color: '#059669', fontWeight: 700, pointerEvents: 'none' }}>✓</div>
      )}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          maxHeight: 200, overflowY: 'auto',
          background: '#fff', border: '1px solid var(--gray-200)', borderRadius: '0 0 6px 6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '8px 10px', color: '#999', fontSize: '0.78rem', fontStyle: 'italic' }}>
              Akun tidak ditemukan
            </div>
          ) : filtered.map(c => (
            <div key={c.id}
              onClick={() => { onSelect(String(c.id)); setOpen(false); setQuery(''); }}
              style={{
                padding: '7px 10px', cursor: 'pointer', fontSize: '0.8rem',
                borderBottom: '1px solid #f0f0f0',
                background: Number(value) === c.id ? '#e0f2fe' : '#fff',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
              onMouseLeave={e => e.currentTarget.style.background = Number(value) === c.id ? '#e0f2fe' : '#fff'}
            >
              <strong style={{ fontSize: '0.82rem' }}>{c.kode_akun}</strong>
              <span style={{ color: '#525f7f', marginLeft: 8, fontSize: '0.78rem' }}>{c.nama_akun}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function JurnalUmum() {
  const { hasMenuAction } = useAuth();
  const navigate = useNavigate();
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  // Navigasi silang: jurnal Penerimaan -> detail penerimaan/PO
  const [detailRef, setDetailRef] = useState(null); // { jenis: 'penerimaan'|'po', penerimaan, po }
  const [filterTglMulai, setFilterTglMulai] = useState('');
  const [filterTglSelesai, setFilterTglSelesai] = useState('');
  const [filterSumber, setFilterSumber] = useState('');
  const [coaList, setCoaList] = useState([]);
  const [form, setForm] = useState({ tanggal: new Date().toISOString().slice(0, 10), keterangan: '', items: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Tab aktif: 'daftar' | 'manual' | 'otomatis'
  const [activeTab, setActiveTab] = useState('daftar');

  // Tab Jurnal Otomatis
  const [kandidat, setKandidat] = useState(null);
  const [autoLoading, setAutoLoading] = useState(false);
  const [autoMsg, setAutoMsg] = useState('');
  const [autoFilter, setAutoFilter] = useState('tagihan'); // tagihan | pembelian | penerimaan | gaji
  const [autoTipeTagihan, setAutoTipeTagihan] = useState('tagih'); // tagih | bayar

  const load = () => {
    setLoading(true);
    const params = {};
    if (filterTglMulai) params.tanggal_mulai = filterTglMulai;
    if (filterTglSelesai) params.tanggal_selesai = filterTglSelesai;
    // Ambil semua sumber sekaligus (filter sumber dilakukan client-side agar
    // kartu ringkasan per sumber tetap akurat saat filter aktif)
    Promise.all([getJurnal(params), getCOA()])
      .then(([res, coaRes]) => {
        setAllData(res.data || []);
        setCoaList(coaRes.data || []);
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Data tampilan = hasil filter sumber (client-side)
  const data = filterSumber ? allData.filter(j => j.sumber === filterSumber) : allData;

  // Ringkasan jumlah & nominal per sumber (dari seluruh data sesuai filter tanggal)
  const sourceSummary = {};
  allData.forEach(j => {
    const s = j.sumber || 'Lain-lain';
    if (!sourceSummary[s]) sourceSummary[s] = { count: 0, debit: 0, kredit: 0 };
    sourceSummary[s].count += 1;
    (j.items || []).forEach(i => {
      sourceSummary[s].debit += Number(i.debit) || 0;
      sourceSummary[s].kredit += Number(i.kredit) || 0;
    });
  });
  const summaryOrder = ['Manual', 'Tagihan', 'Pembelian', 'Penerimaan', 'Retur', 'Gaji', 'Lain-lain'];

  const rupiah = (val) => {
    const n = Number(val);
    return isNaN(n) ? 'Rp 0' : `Rp ${n.toLocaleString('id-ID')}`;
  };

  // Opsi filter sumber jurnal (dari helper agar warnanya konsisten)
  const sumberOptions = ['', 'Manual', 'Tagihan', 'Pembelian', 'Penerimaan', 'Retur', 'Gaji', 'Lain-lain'];

  // ===== Navigasi silang: detail penerimaan / PO dari jurnal Penerimaan =====
  const openPenerimaanDetail = async (j) => {
    if (!j || !j.ref_id) return;
    try {
      const res = await getPenerimaanPembelianById(j.ref_id);
      setDetailRef({ jenis: 'penerimaan', penerimaan: res.data, po: null });
    } catch (err) { alert('Gagal memuat detail penerimaan'); }
  };

  const openPODariPenerimaan = async () => {
    if (!detailRef || !detailRef.penerimaan?.po_id) return;
    try {
      const res = await getPurchaseOrderById(detailRef.penerimaan.po_id);
      setDetailRef({ ...detailRef, jenis: 'po', po: res.data });
    } catch (err) { alert('Gagal memuat detail PO'); }
  };

  const MetodeChip = ({ j }) => {
    if (!j || !j.metode_pembayaran) return null;
    return (
      <span style={{
        display: 'inline-block', marginLeft: 6, padding: '1px 8px', borderRadius: 10,
        fontSize: '0.65rem', fontWeight: 600, whiteSpace: 'nowrap',
        background: '#ede9fe', color: '#5b21b6', verticalAlign: 'middle',
      }}>Metode: {j.metode_pembayaran}</span>
    );
  };

  const addItem = () => {
    setForm(f => ({ ...f, items: [...f.items, { akun_id: '', debit: '', kredit: '', keterangan: '' }] }));
  };

  const updateItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, items });
  };

  const removeItem = (idx) => {
    setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };

  const totalDebit = form.items.reduce((s, i) => s + (Number(i.debit) || 0), 0);
  const totalKredit = form.items.reduce((s, i) => s + (Number(i.kredit) || 0), 0);

  const handleSave = async () => {
    setError('');
    if (!form.tanggal) { setError('Tanggal harus diisi'); return; }
    if (form.items.length === 0) { setError('Minimal 1 item'); return; }
    if (totalDebit !== totalKredit) { setError('Total debit harus sama dengan total kredit'); return; }
    setSaving(true);
    try {
      const payload = {
        tanggal: form.tanggal,
        keterangan: form.keterangan,
        items: form.items.map(i => ({
          akun_id: Number(i.akun_id),
          debit: Number(i.debit) || 0,
          kredit: Number(i.kredit) || 0,
          keterangan: i.keterangan || '',
        })),
      };
      await createJurnal(payload);
      setForm({ tanggal: new Date().toISOString().slice(0, 10), keterangan: '', items: [{ akun_id: '', debit: '', kredit: '', keterangan: '' }] });
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  // ===== Mode Semi Manual (In / Out) =====
  const [jurnalMode, setJurnalMode] = useState('penuh'); // 'penuh' | 'semi'
  const [semiForm, setSemiForm] = useState({
    tanggal: new Date().toISOString().slice(0, 10),
    keterangan: '',
    ins: [{ akun_id: '', nominal: '' }],
    outs: [{ akun_id: '', nominal: '' }],
  });

  const updateSemi = (side, idx, field, value) => {
    const key = side === 'in' ? 'ins' : 'outs';
    const rows = [...semiForm[key]];
    rows[idx] = { ...rows[idx], [field]: value };
    setSemiForm({ ...semiForm, [key]: rows });
  };
  const addSemiRow = (side) => {
    const key = side === 'in' ? 'ins' : 'outs';
    setSemiForm({ ...semiForm, [key]: [...semiForm[key], { akun_id: '', nominal: '' }] });
  };
  const removeSemiRow = (side, idx) => {
    const key = side === 'in' ? 'ins' : 'outs';
    const rows = semiForm[key];
    if (rows.length <= 1) return;
    setSemiForm({ ...semiForm, [key]: rows.filter((_, i) => i !== idx) });
  };

  const totalIn = semiForm.ins.reduce((s, i) => s + (Number(i.nominal) || 0), 0);
  const totalOut = semiForm.outs.reduce((s, i) => s + (Number(i.nominal) || 0), 0);

  const handleSaveSemi = async () => {
    setError('');
    if (!semiForm.tanggal) { setError('Tanggal harus diisi'); return; }
    if (totalIn === 0 && totalOut === 0) { setError('Minimal 1 baris In atau Out'); return; }
    if (totalIn !== totalOut) { setError('Total In harus sama dengan Total Out'); return; }
    setSaving(true);
    try {
      const items = [
        ...semiForm.ins.filter(i => Number(i.nominal) > 0).map(i => ({
          akun_id: Number(i.akun_id),
          debit: Number(i.nominal) || 0,
          kredit: 0,
          keterangan: 'In',
        })),
        ...semiForm.outs.filter(o => Number(o.nominal) > 0).map(o => ({
          akun_id: Number(o.akun_id),
          debit: 0,
          kredit: Number(o.nominal) || 0,
          keterangan: 'Out',
        })),
      ];
      if (items.some(i => !i.akun_id)) { setError('Semua baris In/Out harus memilih akun'); return; }
      await createJurnal({ tanggal: semiForm.tanggal, keterangan: semiForm.keterangan, items });
      setSemiForm({ tanggal: new Date().toISOString().slice(0, 10), keterangan: '', ins: [{ akun_id: '', nominal: '' }], outs: [{ akun_id: '', nominal: '' }] });
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus jurnal ini?')) return;
    try { await deleteJurnal(id); load(); }
    catch (err) { alert(err.response?.data?.error || 'Gagal menghapus'); }
  };

  // ===== PDF Export (Laporan Keuangan Format) =====
  const generateAllPDF = () => {
    if (!data.length) return;
    const { jsPDF } = require('jspdf');
    const autoTable = require('jspdf-autotable').default || require('jspdf-autotable');
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentW = pageW - 2 * margin;

    // Color palette
    const primary = [17, 205, 239];
    const primaryDark = [0, 150, 180];
    const dark = [23, 43, 77];
    const gray = [136, 152, 170];
    const lightGray = [248, 249, 252];
    const white = [255, 255, 255];

    // Calculate totals
    let totalDebitAll = 0, totalKreditAll = 0;
    const manualCount = data.filter(j => j.sumber === 'Manual').length;
    const tagihanCount = data.filter(j => j.sumber === 'Tagihan').length;
    data.forEach(j => j.items.forEach(i => { totalDebitAll += i.debit; totalKreditAll += i.kredit; }));
    const balance = totalDebitAll - totalKreditAll;
    const dateRange = filterTglMulai && filterTglSelesai ? `${filterTglMulai} s.d ${filterTglSelesai}` : 'Semua Periode';

    // === COVER PAGE ===
    // Background accent
    doc.setFillColor(...primary);
    doc.rect(0, 0, pageW, 6, 'F');
    doc.setFillColor(...primaryDark);
    doc.rect(0, 6, pageW, 2, 'F');

    // Company logo area
    doc.setFillColor(...dark);
    doc.roundedRect(margin, 20, 60, 35, 4, 4, 'F');
    doc.setTextColor(...white);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('L Synexa', margin + 30, 38, { align: 'center' });
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text('Sistem Informasi Armada', margin + 30, 46, { align: 'center' });

    // Report title
    doc.setTextColor(...dark);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('LAPORAN JURNAL UMUM', margin + 70, 30);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...gray);
    doc.text('General Journal Report', margin + 70, 40);

    // Decorative line
    doc.setDrawColor(...primary);
    doc.setLineWidth(2);
    doc.line(margin, 62, pageW - margin, 62);
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.5);
    doc.line(margin, 64, pageW - margin, 64);

    // Report info box
    doc.setFillColor(...lightGray);
    doc.roundedRect(margin, 70, contentW, 30, 3, 3, 'F');
    doc.setDrawColor(230, 230, 230);
    doc.roundedRect(margin, 70, contentW, 30, 3, 3, 'S');

    doc.setTextColor(...dark);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('INFORMASI LAPORAN', margin + 8, 82);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...gray);
    doc.text(`Periode: ${dateRange}`, margin + 8, 90);
    doc.text(`Dicetak: ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`, margin + 8, 96);
    doc.text(`Total Transaksi: ${data.length} jurnal`, margin + contentW / 2, 90);
    doc.text(`Status: ${balance === 0 ? 'Balance ✓' : 'Selisih: Rp ' + balance.toLocaleString('id-ID')}`, margin + contentW / 2, 96);

    // Summary cards
    const cardY = 108;
    const cardW = (contentW - 20) / 4;
    const cards = [
      { label: 'TOTAL JURNAL', value: `${data.length}`, color: dark },
      { label: 'JURNAL MANUAL', value: `${manualCount}`, color: [94, 114, 228] },
      { label: 'DARI TAGIHAN', value: `${tagihanCount}`, color: primary },
      { label: 'TOTAL NOMINAL', value: `Rp ${totalDebitAll.toLocaleString('id-ID')}`, color: [16, 185, 129] },
    ];
    cards.forEach((card, i) => {
      const x = margin + i * (cardW + 5) + (i === 0 ? 0 : 0);
      doc.setFillColor(...white);
      doc.roundedRect(x, cardY, cardW, 28, 3, 3, 'F');
      doc.setDrawColor(...card.color);
      doc.setLineWidth(1.5);
      doc.line(x + 5, cardY + 3, x + 5, cardY + 25);
      doc.setLineWidth(0.5);
      doc.setTextColor(...gray);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.text(card.label, x + 12, cardY + 12);
      doc.setTextColor(...card.color);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(card.value, x + 12, cardY + 22);
    });

    // === RINGKASAN PER SUMBER ===
    const srcAgg = {};
    const srcPriority = ['Manual', 'Tagihan', 'Pembelian', 'Penerimaan', 'Retur', 'Gaji', 'Lain-lain'];
    data.forEach(j => {
      const s = j.sumber || 'Lain-lain';
      if (!srcAgg[s]) srcAgg[s] = { count: 0, debit: 0, kredit: 0 };
      srcAgg[s].count += 1;
      (j.items || []).forEach(i => {
        srcAgg[s].debit += i.debit || 0;
        srcAgg[s].kredit += i.kredit || 0;
      });
    });
    const srcRows = Object.keys(srcAgg)
      .sort((a, b) => {
        const ia = srcPriority.indexOf(a), ib = srcPriority.indexOf(b);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      })
      .map(s => [s, srcAgg[s].count, srcAgg[s].debit.toLocaleString('id-ID'), srcAgg[s].kredit.toLocaleString('id-ID')]);

    let summaryY = 145;
    doc.setTextColor(...dark);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('RINGKASAN PER SUMBER', margin, summaryY);
    doc.setFillColor(...primary);
    doc.rect(margin, summaryY + 3, 50, 1.5, 'F');

    autoTable(doc, {
      startY: summaryY + 8,
      margin: { left: margin, right: margin },
      head: [['Sumber', 'Jumlah Jurnal', 'Total Debit (Rp)', 'Total Kredit (Rp)']],
      body: [...srcRows, ['TOTAL', data.length, totalDebitAll.toLocaleString('id-ID'), totalKreditAll.toLocaleString('id-ID')]],
      styles: { fontSize: 8, cellPadding: 3, lineColor: [220, 220, 220], lineWidth: 0.5, textColor: [50, 50, 50] },
      headStyles: { fillColor: [...dark], textColor: [...white], fontStyle: 'bold', fontSize: 8, halign: 'center' },
      alternateRowStyles: { fillColor: [245, 248, 252] },
      columnStyles: {
        0: { fontStyle: 'bold', textColor: [...dark], cellWidth: 45 },
        1: { halign: 'center' },
        2: { halign: 'right', fontStyle: 'bold', textColor: [5, 150, 105] },
        3: { halign: 'right', fontStyle: 'bold', textColor: [220, 38, 38] },
      },
      footStyles: { fillColor: [240, 253, 244], textColor: [...dark], fontStyle: 'bold', fontSize: 8 },
    });

    // === RINGKASAN PPN (PAJAK PERTAMBAHAN NILAI) ===
    // Agregasi PPN Masukan (akun 1500) per sumber jurnal.
    const ppnAgg = {};
    data.forEach(j => {
      (j.items || []).forEach(i => {
        if (i.kode_akun === '1500' && Number(i.debit) > 0) {
          const s = j.sumber || 'Lain-lain';
          if (!ppnAgg[s]) ppnAgg[s] = { set: new Set(), debit: 0 };
          ppnAgg[s].set.add(j.id || j.no_jurnal);
          ppnAgg[s].debit += Number(i.debit) || 0;
        }
      });
    });
    const ppnRows = Object.keys(ppnAgg)
      .sort((a, b) => {
        const ia = srcPriority.indexOf(a), ib = srcPriority.indexOf(b);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      })
      .map(s => [s, ppnAgg[s].set.size, ppnAgg[s].debit.toLocaleString('id-ID')]);
    const ppnJurnalCount = Object.values(ppnAgg).reduce((a, b) => a + b.set.size, 0);
    const ppnTotal = Object.values(ppnAgg).reduce((a, b) => a + b.debit, 0);

    let ppnY = doc.lastAutoTable.finalY + 14;
    doc.setTextColor(...dark);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('RINGKASAN PPN (PAJAK PERTAMBAHAN NILAI)', margin, ppnY);
    doc.setFillColor(...primary);
    doc.rect(margin, ppnY + 3, 50, 1.5, 'F');

    if (ppnRows.length === 0) {
      doc.setTextColor(...gray);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('Tidak ada transaksi PPN Masukan (akun 1500) pada periode ini.', margin, ppnY + 10);
    } else {
      autoTable(doc, {
        startY: ppnY + 8,
        margin: { left: margin, right: margin },
        head: [['Sumber', 'Jml. Jurnal Ber-PPN', 'PPN Masukan (Rp)']],
        body: [...ppnRows, ['TOTAL', ppnJurnalCount, ppnTotal.toLocaleString('id-ID')]],
        styles: { fontSize: 8, cellPadding: 3, lineColor: [220, 220, 220], lineWidth: 0.5, textColor: [50, 50, 50] },
        headStyles: { fillColor: [...dark], textColor: [...white], fontStyle: 'bold', fontSize: 8, halign: 'center' },
        alternateRowStyles: { fillColor: [250, 245, 255] },
        columnStyles: {
          0: { fontStyle: 'bold', textColor: [...dark], cellWidth: 60 },
          1: { halign: 'center' },
          2: { halign: 'right', fontStyle: 'bold', textColor: [124, 58, 237] },
        },
        footStyles: { fillColor: [245, 243, 255], textColor: [...dark], fontStyle: 'bold', fontSize: 8 },
      });
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(7);
      doc.setTextColor(...gray);
      doc.text('PPN Masukan = akun 1500 (PPN yang dapat dikreditkan dari pembelian/penerimaan ber-PPN).', margin, doc.lastAutoTable.finalY + 5);
    }

    // === DETAIL TRANSAKSI ===
    const detailY = doc.lastAutoTable.finalY + 14;
    doc.setTextColor(...dark);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('DETAIL TRANSAKSI', margin, detailY);
    doc.setFillColor(...primary);
    doc.rect(margin, detailY + 3, 40, 1.5, 'F');

    // Flatten jurnal items into table rows
    const headers = [['No. Jurnal', 'Tanggal', 'Sumber', 'Keterangan', 'Akun Perkiraan', 'Kode', 'Debit (Rp)', 'Kredit (Rp)']];
    const rows = [];
    data.forEach(j => {
      if (j.items.length === 0) {
        rows.push([j.no_jurnal, j.tanggal?.slice(0, 10) || '-', j.sumber, j.keterangan || '-', '-', '-', '-', '-']);
      } else {
        j.items.forEach((item, idx) => {
          rows.push([
            idx === 0 ? j.no_jurnal : '',
            idx === 0 ? (j.tanggal?.slice(0, 10) || '-') : '',
            idx === 0 ? j.sumber : '',
            idx === 0 ? (j.keterangan || '-') : '',
            `${item.kode_akun} - ${item.nama_akun}`,
            item.kode_akun || '-',
            item.debit > 0 ? item.debit.toLocaleString('id-ID') : '',
            item.kredit > 0 ? item.kredit.toLocaleString('id-ID') : '',
          ]);
        });
      }
    });

    // Add totals row
    rows.push(['', '', '', 'TOTAL', '', '', totalDebitAll.toLocaleString('id-ID'), totalKreditAll.toLocaleString('id-ID')]);

    autoTable(doc, {
      startY: detailY + 10,
      margin: { left: margin, right: margin },
      head: headers,
      body: rows,
      styles: { fontSize: 7, cellPadding: 2.5, lineColor: [220, 220, 220], lineWidth: 0.5, textColor: [50, 50, 50] },
      headStyles: { fillColor: [...dark], textColor: [...white], fontStyle: 'bold', fontSize: 7, halign: 'center' },
      bodyStyles: { fontSize: 7 },
      alternateRowStyles: { fillColor: [245, 248, 252] },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 35, textColor: [...dark] },
        1: { cellWidth: 22, halign: 'center' },
        2: { cellWidth: 18, halign: 'center' },
        3: { cellWidth: 50 },
        4: { cellWidth: 55 },
        5: { halign: 'center', cellWidth: 18 },
        6: { halign: 'right', fontStyle: 'bold', textColor: [5, 150, 105] },
        7: { halign: 'right', fontStyle: 'bold', textColor: [220, 38, 38] },
      },
      didParseCell: function(data) {
        // Style totals row
        if (data.row.index === rows.length - 1) {
          data.cell.styles.fillColor = [240, 253, 244];
          data.cell.styles.textColor = [...dark];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.lineWidth = 0.8;
          data.cell.styles.lineColor = [16, 185, 129];
        }
        // Bold first column (no jurnal)
        if (data.column.index === 0 && data.cell.raw && data.row.raw[0]) {
          data.cell.styles.textColor = [...primaryDark];
        }
      },
      footStyles: { fillColor: [240, 253, 244], textColor: [...dark], fontStyle: 'bold', fontSize: 7 },
    });

    // Footer on all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      // Bottom line
      doc.setDrawColor(...primary);
      doc.setLineWidth(1);
      doc.line(margin, pageH - 20, pageW - margin, pageH - 20);
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.3);
      doc.line(margin, pageH - 19, pageW - margin, pageH - 19);

      doc.setTextColor(...gray);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.text(`Layanan Synexa - Sistem Informasi Armada`, margin, pageH - 14);
      doc.text(`Dicetak: ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`, margin, pageH - 9);
      doc.setFont('helvetica', 'normal');
      doc.text(`Halaman ${i} dari ${pageCount}`, pageW - margin, pageH - 9, { align: 'right' });
    }

    doc.save('Laporan_Jurnal_Umum.pdf');
  };

  const generateJurnalPDF = (j) => {
    if (!j) return;
    const { jsPDF } = require('jspdf');
    const autoTable = require('jspdf-autotable').default || require('jspdf-autotable');
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentW = pageW - 2 * margin;

    // Color palette
    const primary = [17, 205, 239];
    const primaryDark = [0, 150, 180];
    const dark = [23, 43, 77];
    const gray = [136, 152, 170];
    const lightGray = [248, 249, 252];
    const white = [255, 255, 255];

    // Calculate totals
    let totalD = 0, totalK = 0;
    j.items.forEach(i => { totalD += i.debit; totalK += i.kredit; });
    const balance = totalD - totalK;

    // === HEADER ===
    // Top accent bar
    doc.setFillColor(...primary);
    doc.rect(0, 0, pageW, 6, 'F');
    doc.setFillColor(...primaryDark);
    doc.rect(0, 6, pageW, 2, 'F');

    // Company logo box
    doc.setFillColor(...dark);
    doc.roundedRect(margin, 16, 50, 30, 4, 4, 'F');
    doc.setTextColor(...white);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('L Synexa', margin + 25, 30, { align: 'center' });
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.text('Sistem Informasi Armada', margin + 25, 37, { align: 'center' });

    // Title
    doc.setTextColor(...dark);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('JURNAL UMUM', margin + 58, 25);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...gray);
    doc.text('General Journal Entry', margin + 58, 33);
    doc.text(`No. ${j.no_jurnal || '-'}`, margin + 58, 40);

    // Sumber badge (warna konsisten dgn halaman — hex ke rgb)
    const srcInfo = sumberInfo(j.sumber);
    const hexToRgb = (hex) => [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
    const sumberColor = hexToRgb(srcInfo.bg);
    const sumberTextColor = hexToRgb(srcInfo.color);
    doc.setFillColor(...sumberColor);
    doc.roundedRect(pageW - margin - 30, 18, 30, 10, 3, 3, 'F');
    doc.setTextColor(...sumberTextColor);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(j.sumber || '-', pageW - margin - 15, 25, { align: 'center' });

    // Decorative line
    doc.setDrawColor(...primary);
    doc.setLineWidth(1.5);
    doc.line(margin, 52, pageW - margin, 52);
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.3);
    doc.line(margin, 54, pageW - margin, 54);

    // === INFO SECTION ===
    let y = 62;
    doc.setFillColor(...lightGray);
    doc.roundedRect(margin, y - 4, contentW, 38, 3, 3, 'F');
    doc.setDrawColor(230, 230, 230);
    doc.roundedRect(margin, y - 4, contentW, 38, 3, 3, 'S');

    doc.setTextColor(...dark);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('INFORMASI TRANSAKSI', margin + 8, y + 6);
    doc.setFillColor(...primary);
    doc.rect(margin + 8, y + 9, 25, 1, 'F');

    y += 16;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...gray);

    // Left column
    doc.text('No. Jurnal:', margin + 8, y);
    doc.setTextColor(...dark);
    doc.setFont('helvetica', 'bold');
    doc.text(j.no_jurnal || '-', margin + 40, y);

    doc.setTextColor(...gray);
    doc.setFont('helvetica', 'normal');
    doc.text('Tanggal:', margin + 8, y + 6);
    doc.setTextColor(...dark);
    doc.text(j.tanggal?.slice(0, 10) || '-', margin + 40, y + 6);

    // Right column
    doc.setTextColor(...gray);
    doc.text('Sumber:', margin + contentW / 2 + 8, y);
    doc.setTextColor(...dark);
    doc.setFont('helvetica', 'bold');
    doc.text(j.sumber || '-', margin + contentW / 2 + 40, y);

    doc.setTextColor(...gray);
    doc.setFont('helvetica', 'normal');
    doc.text('Status:', margin + contentW / 2 + 8, y + 6);
    doc.setTextColor(...(balance === 0 ? [16, 185, 129] : [220, 38, 38]));
    doc.setFont('helvetica', 'bold');
    doc.text(balance === 0 ? 'Balance ✓' : 'Selisih: Rp ' + balance.toLocaleString('id-ID'), margin + contentW / 2 + 40, y + 6);

    if (j.keterangan) {
      doc.setTextColor(...gray);
      doc.setFont('helvetica', 'normal');
      doc.text('Keterangan:', margin + 8, y + 12);
      doc.setTextColor(...dark);
      doc.text(j.keterangan, margin + 40, y + 12);
    }

    y += 24;

    // === SUMMARY BOX ===
    const sumBoxY = y;
    const sumBoxW = contentW / 3 - 3;
    doc.setFillColor(...white);
    doc.roundedRect(margin, sumBoxY, sumBoxW, 22, 3, 3, 'F');
    doc.setDrawColor(16, 185, 129);
    doc.setLineWidth(1);
    doc.line(margin + 2, sumBoxY + 3, margin + 2, sumBoxY + 19);
    doc.setLineWidth(0.3);
    doc.setTextColor(...gray);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text('TOTAL DEBIT', margin + 8, sumBoxY + 10);
    doc.setTextColor(...[16, 185, 129]);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`Rp ${totalD.toLocaleString('id-ID')}`, margin + 8, sumBoxY + 18);

    doc.setFillColor(...white);
    doc.roundedRect(margin + sumBoxW + 5, sumBoxY, sumBoxW, 22, 3, 3, 'F');
    doc.setDrawColor(220, 38, 38);
    doc.setLineWidth(1);
    doc.line(margin + sumBoxW + 7, sumBoxY + 3, margin + sumBoxW + 7, sumBoxY + 19);
    doc.setLineWidth(0.3);
    doc.setTextColor(...gray);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text('TOTAL KREDIT', margin + sumBoxW + 13, sumBoxY + 10);
    doc.setTextColor(...[220, 38, 38]);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`Rp ${totalK.toLocaleString('id-ID')}`, margin + sumBoxW + 13, sumBoxY + 18);

    doc.setFillColor(...white);
    doc.roundedRect(margin + 2 * (sumBoxW + 5), sumBoxY, sumBoxW, 22, 3, 3, 'F');
    doc.setDrawColor(...(balance === 0 ? [16, 185, 129] : [220, 38, 38]));
    doc.setLineWidth(1);
    doc.line(margin + 2 * (sumBoxW + 5) + 2, sumBoxY + 3, margin + 2 * (sumBoxW + 5) + 2, sumBoxY + 19);
    doc.setLineWidth(0.3);
    doc.setTextColor(...gray);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text('SELISIH', margin + 2 * (sumBoxW + 5) + 8, sumBoxY + 10);
    doc.setTextColor(...(balance === 0 ? [16, 185, 129] : [220, 38, 38]));
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`Rp ${Math.abs(balance).toLocaleString('id-ID')}`, margin + 2 * (sumBoxW + 5) + 8, sumBoxY + 18);

    y += 30;

    // === DETAIL TABLE ===
    doc.setTextColor(...dark);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('DETAIL JURNAL', margin, y);
    doc.setFillColor(...primary);
    doc.rect(margin, y + 3, 35, 1.5, 'F');
    y += 10;

    const itemHeaders = [['No', 'Kode Akun', 'Nama Akun', 'Debit (Rp)', 'Kredit (Rp)', 'Keterangan']];
    const itemRows = j.items.map((item, idx) => [
      (idx + 1).toString(),
      item.kode_akun || '-',
      item.nama_akun || '-',
      item.debit > 0 ? item.debit.toLocaleString('id-ID') : '',
      item.kredit > 0 ? item.kredit.toLocaleString('id-ID') : '',
      item.keterangan || '-',
    ]);

    // Add totals row
    itemRows.push(['', '', 'TOTAL', totalD.toLocaleString('id-ID'), totalK.toLocaleString('id-ID'), '']);

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: itemHeaders,
      body: itemRows,
      styles: { fontSize: 8, cellPadding: 3, lineColor: [220, 220, 220], lineWidth: 0.5, textColor: [50, 50, 50] },
      headStyles: { fillColor: [...dark], textColor: [...white], fontStyle: 'bold', fontSize: 8, halign: 'center' },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [245, 248, 252] },
      columnStyles: {
        0: { halign: 'center', cellWidth: 12 },
        1: { halign: 'center', cellWidth: 22, fontStyle: 'bold' },
        2: { fontStyle: 'bold', textColor: [...dark] },
        3: { halign: 'right', textColor: [5, 150, 105], fontStyle: 'bold' },
        4: { halign: 'right', textColor: [220, 38, 38], fontStyle: 'bold' },
        5: { fontSize: 7 },
      },
      didParseCell: function(data) {
        // Style totals row
        if (data.row.index === itemRows.length - 1) {
          data.cell.styles.fillColor = [240, 253, 244];
          data.cell.styles.textColor = [...dark];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.lineWidth = 0.8;
          data.cell.styles.lineColor = [16, 185, 129];
        }
      },
      footStyles: { fillColor: [240, 253, 244], textColor: [...dark], fontStyle: 'bold', fontSize: 8 },
    });

    // === SIGNATURE SECTION ===
    const sigY = pageH - 55;
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(margin, sigY - 5, pageW - margin, sigY - 5);

    doc.setTextColor(...gray);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    const now = new Date();
    const dateStr = now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
    doc.text(`Dicetak pada: ${dateStr}`, margin, sigY);

    // Prepared by
    const sigWidth = 50;
    const sigX1 = pageW - margin - 2 * sigWidth - 15;
    const sigX2 = pageW - margin - sigWidth;

    doc.setTextColor(...dark);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('Disiapkan oleh:', sigX1, sigY);
    doc.text('Diketahui oleh:', sigX2, sigY);

    // Signature lines
    doc.setDrawColor(...dark);
    doc.setLineWidth(0.5);
    doc.line(sigX1, sigY + 15, sigX1 + sigWidth, sigY + 15);
    doc.line(sigX2, sigY + 15, sigX2 + sigWidth, sigY + 15);

    // Names
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.text('(...........................)', sigX1 + sigWidth / 2, sigY + 20, { align: 'center' });
    doc.text('(...........................)', sigX2 + sigWidth / 2, sigY + 20, { align: 'center' });

    // Titles
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...gray);
    doc.setFontSize(6);
    doc.text('Staff Administrasi', sigX1 + sigWidth / 2, sigY + 25, { align: 'center' });
    doc.text('Kepala Bagian Keuangan', sigX2 + sigWidth / 2, sigY + 25, { align: 'center' });

    // Footer
    doc.setDrawColor(...primary);
    doc.setLineWidth(1);
    doc.line(margin, pageH - 12, pageW - margin, pageH - 12);
    doc.setTextColor(...gray);
    doc.setFontSize(6);
    doc.setFont('helvetica', 'italic');
    doc.text('Layanan Synexa - Sistem Informasi Armada', margin, pageH - 7);
    doc.text('Halaman 1 dari 1', pageW - margin, pageH - 7, { align: 'right' });

    doc.save(`Jurnal_${j.no_jurnal || 'umum'}.pdf`);
  };

  // ===== Tab Jurnal Otomatis =====
  const loadKandidat = () => {
    setAutoLoading(true);
    setAutoMsg('');
    getKandidatJurnalOtomatis()
      .then(res => setKandidat(res.data || {}))
      .catch(() => setKandidat({}))
      .finally(() => setAutoLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeTab === 'otomatis') loadKandidat(); }, [activeTab]);

  const handleBuatJurnalOtomatis = async (sumber, id) => {
    setSaving(true);
    setAutoMsg('');
    try {
      if (sumber === 'tagihan') {
        await createJurnalDariTagihan({ tagihan_id: id, tipe: autoTipeTagihan });
      } else if (sumber === 'pembelian') {
        await createJurnalDariPembelian({ pembelian_id: id });
      } else if (sumber === 'penerimaan') {
        await createJurnalDariPenerimaan({ penerimaan_id: id });
      } else if (sumber === 'gaji') {
        await createJurnalDariGaji({ slip_id: id });
      }
      setAutoMsg('✓ Jurnal berhasil dibuat');
      loadKandidat();
      load();
    } catch (err) {
      setAutoMsg('✗ ' + (err.response?.data?.error || 'Gagal membuat jurnal'));
    } finally { setSaving(false); }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h2>Jurnal Umum</h2>
        <p className="page-desc">Input jurnal manual atau buat jurnal otomatis dari transaksi (tagihan, pembelian, penerimaan, gaji)</p>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid var(--gray-200)', marginBottom: 16 }}>
        {[
          { key: 'daftar', label: 'Daftar Jurnal', icon: BookOpen },
          { key: 'manual', label: 'Jurnal Manual', icon: Plus },
          // { key: 'otomatis', label: 'Jurnal Otomatis', icon: Zap }, // disembunyikan sementara
        ].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', cursor: 'pointer',
            background: activeTab === t.key ? '#fff' : 'transparent', border: 'none',
            borderBottom: activeTab === t.key ? '2.5px solid #11cdef' : '2.5px solid transparent',
            fontWeight: activeTab === t.key ? 700 : 500, fontSize: '0.85rem',
            color: activeTab === t.key ? '#0e7490' : 'var(--gray-500)', borderRadius: '6px 6px 0 0',
          }}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'daftar' && (
        <>
      <div className="page-header-row" style={{ marginBottom: 16 }}>
        <div className="page-header-actions">
          {data.length > 0 && (
            <button className="btn btn-secondary" onClick={generateAllPDF}>
              <Printer size={16} /><span>Export PDF</span>
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={filterSumber} onChange={e => { setFilterSumber(e.target.value); setTimeout(load, 0); }}
            style={{ padding: '6px 10px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius)', fontSize: '0.85rem', background: '#fff' }}>
            <option value="">Semua Sumber</option>
            {sumberOptions.filter(Boolean).map(s => (
              <option key={s} value={s} style={{ background: sumberInfo(s).bg, color: sumberInfo(s).color }}>{sumberInfo(s).label}</option>
            ))}
          </select>
          <input type="date" value={filterTglMulai} onChange={e => setFilterTglMulai(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius)', fontSize: '0.85rem' }} />
          <span style={{ color: 'var(--gray-400)' }}>-</span>
          <input type="date" value={filterTglSelesai} onChange={e => setFilterTglSelesai(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius)', fontSize: '0.85rem' }} />
          <button className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '0.8rem' }} onClick={load}>
            <Search size={14} /> Filter
          </button>
        </div>
      </div>

      {/* Kartu ringkasan per sumber (klik untuk memfilter) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
        <div onClick={() => setFilterSumber('')}
          style={{
            background: '#fff', borderRadius: 10, border: `1.5px solid ${filterSumber === '' ? '#11cdef' : 'var(--gray-100)'}`,
            padding: '10px 14px', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}
          onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}>
          <div style={{ fontSize: '0.68rem', color: 'var(--gray-400)', fontWeight: 600, textTransform: 'uppercase' }}>Total Jurnal</div>
          <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0ea5e9' }}>{allData.length}</div>
          <div style={{ fontSize: '0.65rem', color: 'var(--gray-400)' }}>{rupiah(allData.reduce((s, j) => s + (j.items || []).reduce((x, i) => x + (Number(i.debit) || 0), 0), 0))}</div>
        </div>
        {summaryOrder.filter(s => sourceSummary[s]).map(s => {
          const info = sumberInfo(s);
          const active = filterSumber === s;
          const item = sourceSummary[s];
          return (
            <div key={s} onClick={() => setFilterSumber(active ? '' : s)}
              style={{
                background: '#fff', borderRadius: 10, border: `1.5px solid ${active ? info.color : 'var(--gray-100)'}`,
                padding: '10px 14px', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', transition: 'all 0.15s',
                borderLeft: `4px solid ${info.color}`,
              }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}>
              <div style={{ fontSize: '0.68rem', color: info.color, fontWeight: 700, textTransform: 'uppercase' }}>{info.label}</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 800, color: info.color }}>{item.count}</div>
              <div style={{ fontSize: '0.65rem', color: 'var(--gray-400)' }}>{rupiah(item.debit)}</div>
            </div>
          );
        })}
      </div>

      {loading ? <div className="page-loading">Memuat jurnal...</div> : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>No Jurnal</th>
                <th>Tanggal</th>
                <th>Sumber</th>
                <th>Keterangan</th>
                <th>Akun</th>
                <th>Debit</th>
                <th>Kredit</th>
                <th style={{ width: 60 }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {data.map(j => (
                <React.Fragment key={j.id}>
                  {j.items.length === 0 ? (
                    <tr>
                      <td><strong>{j.no_jurnal}</strong></td>
                      <td>{j.tanggal}</td>
                      <td><span className="badge" style={{ ...badgeSumber(j.sumber), fontSize: '0.7rem' }}>{sumberInfo(j.sumber).label}</span></td>
                      <td colSpan={3}>{j.keterangan}<MetodeChip j={j} /></td>
                      <td>
                        {j.sumber === 'Penerimaan' && j.ref_id && (
                          <button className="btn-icon" title="Lihat penerimaan terkait" onClick={() => openPenerimaanDetail(j)} style={{ color: '#7c3aed' }}><Eye size={14} /></button>
                        )}
                        {hasMenuAction('akuntansi', 'delete') && (
                          <button className="btn-icon btn-danger" title="Hapus jurnal" onClick={() => handleDelete(j.id)}><Trash2 size={14} /></button>
                        )}
                      </td>
                    </tr>
                  ) : (
                    j.items.map((item, idx) => (
                      <tr key={`${j.id}-${idx}`}>
                        {idx === 0 && (
                          <>
                            <td rowSpan={j.items.length}><strong style={{ fontSize: '0.8rem' }}>{j.no_jurnal}</strong></td>
                            <td rowSpan={j.items.length}>{j.tanggal?.slice(0, 10)}</td>
                            <td rowSpan={j.items.length}>
                              <span className="badge" style={{ ...badgeSumber(j.sumber), fontSize: '0.7rem' }}>{sumberInfo(j.sumber).label}</span>
                            </td>
                            <td rowSpan={j.items.length} style={{ fontSize: '0.82rem', maxWidth: 220 }}>{j.keterangan}<MetodeChip j={j} /></td>
                          </>
                        )}
                        <td style={{ fontSize: '0.82rem' }}>
                          <span style={{ fontWeight: 500 }}>{item.kode_akun}</span> - {item.nama_akun}
                        </td>
                        <td style={{ color: item.debit > 0 ? '#065f46' : 'inherit', fontWeight: item.debit > 0 ? 600 : 400 }}>
                          {item.debit > 0 ? rupiah(item.debit) : '-'}
                        </td>
                        <td style={{ color: item.kredit > 0 ? '#92400e' : 'inherit', fontWeight: item.kredit > 0 ? 600 : 400 }}>
                          {item.kredit > 0 ? rupiah(item.kredit) : '-'}
                        </td>
                        {idx === 0 && (
                          <td rowSpan={j.items.length}>
                        {j.sumber === 'Penerimaan' && j.ref_id && (
                          <button className="btn-icon" title="Lihat penerimaan terkait" onClick={() => openPenerimaanDetail(j)} style={{ color: '#7c3aed' }}><Eye size={14} /></button>
                        )}
                        <button className="btn-icon" title="Export PDF" onClick={() => generateJurnalPDF(j)} style={{ color: '#11cdef' }}><Printer size={14} /></button>
                        {hasMenuAction('akuntansi', 'delete') && (
                          <button className="btn-icon btn-danger" title="Hapus jurnal" onClick={() => handleDelete(j.id)}><Trash2 size={14} /></button>
                        )}
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </React.Fragment>
              ))}
              {data.length === 0 && (
                <tr><td colSpan={8} className="text-center" style={{ padding: 40, color: 'var(--gray-400)' }}>Belum ada jurnal</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

        </>
      )}

      {/* ===== Tab Jurnal Manual ===== */}
      {activeTab === 'manual' && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--gray-200)', padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <h3 style={{ marginBottom: 4 }}>Jurnal Manual</h3>
          <p style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginBottom: 16 }}>Catat jurnal umum secara manual — pilih mode penuh (Debit/Kredit) atau semi (In/Out).</p>

          {/* Toggle mode: Penuh vs Semi */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button onClick={() => { setJurnalMode('penuh'); setError(''); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
                border: `1.5px solid ${jurnalMode === 'penuh' ? '#11cdef' : 'var(--gray-200)'}`,
                background: jurnalMode === 'penuh' ? '#ecfeff' : '#fff',
                color: jurnalMode === 'penuh' ? '#0e7490' : 'var(--gray-500)',
                fontWeight: jurnalMode === 'penuh' ? 700 : 500, fontSize: '0.82rem',
              }}>
              <ArrowRight size={14} /> Penuh (Debit / Kredit)
            </button>
            <button onClick={() => { setJurnalMode('semi'); setError(''); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
                border: `1.5px solid ${jurnalMode === 'semi' ? '#8b5cf6' : 'var(--gray-200)'}`,
                background: jurnalMode === 'semi' ? '#f5f3ff' : '#fff',
                color: jurnalMode === 'semi' ? '#6d28d9' : 'var(--gray-500)',
                fontWeight: jurnalMode === 'semi' ? 700 : 500, fontSize: '0.82rem',
              }}>
              <RotateCcw size={14} /> Semi (In / Out)
            </button>
          </div>

          {error && <div className="form-error"><AlertCircle size={14} /><span>{error}</span></div>}

          {jurnalMode === 'penuh' && (
          <>
          <div className="form-grid" style={{ marginBottom: 16 }}>
            <div className="form-group">
              <label>Tanggal</label>
              <input type="date" value={form.tanggal} onChange={e => setForm({...form, tanggal: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Keterangan</label>
              <input type="text" value={form.keterangan} onChange={e => setForm({...form, keterangan: e.target.value})} placeholder="Keterangan jurnal" />
            </div>
          </div>

          <h4 style={{ fontSize: '0.85rem', color: 'var(--gray-400)', textTransform: 'uppercase', marginBottom: 12 }}>Item Jurnal</h4>
          <div style={{ display: 'flex', gap: 8, fontWeight: 600, fontSize: '0.8rem', color: 'var(--gray-400)', marginBottom: 8, padding: '0 4px' }}>
            <span style={{ flex: 2 }}>Akun</span>
            <span style={{ flex: 1, textAlign: 'right' }}>Debit</span>
            <span style={{ flex: 1, textAlign: 'right' }}>Kredit</span>
            <span style={{ flex: 2 }}>Keterangan</span>
            <span style={{ width: 32 }}></span>
          </div>
          {form.items.map((item, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <SearchableAkunSelect coaList={coaList} value={item.akun_id}
                onSelect={id => updateItem(idx, 'akun_id', id)}
                placeholder="Ketik kode / nama akun..." />
              <input type="number" value={item.debit} onChange={e => updateItem(idx, 'debit', e.target.value)}
                placeholder="0" style={{ flex: 1, padding: '6px 10px', border: '1px solid var(--gray-200)', borderRadius: '6px', fontSize: '0.82rem', textAlign: 'right' }} />
              <input type="number" value={item.kredit} onChange={e => updateItem(idx, 'kredit', e.target.value)}
                placeholder="0" style={{ flex: 1, padding: '6px 10px', border: '1px solid var(--gray-200)', borderRadius: '6px', fontSize: '0.82rem', textAlign: 'right' }} />
              <input type="text" value={item.keterangan} onChange={e => updateItem(idx, 'keterangan', e.target.value)}
                placeholder="Ket" style={{ flex: 2, padding: '6px 10px', border: '1px solid var(--gray-200)', borderRadius: '6px', fontSize: '0.82rem' }} />
              <button className="btn-icon btn-danger" onClick={() => removeItem(idx)} disabled={form.items.length === 1}><X size={14} /></button>
            </div>
          ))}
          <button className="btn btn-secondary" style={{ fontSize: '0.8rem', padding: '6px 12px', marginTop: 4 }} onClick={addItem}>
            + Tambah Item
          </button>
          <div style={{ marginTop: 12, padding: '8px 12px', background: '#f8f9fe', borderRadius: 'var(--radius)', display: 'flex', justifyContent: 'space-between', fontWeight: 600, flexWrap: 'wrap', gap: 8 }}>
            <span>Total Debit: <span style={{ color: '#065f46' }}>{rupiah(totalDebit)}</span></span>
            <span>Total Kredit: <span style={{ color: '#92400e' }}>{rupiah(totalKredit)}</span></span>
            <span style={{ color: totalDebit === totalKredit ? '#065f46' : '#dc2626' }}>
              {totalDebit === totalKredit ? '✓ Balance' : `Selisih: ${rupiah(totalDebit - totalKredit)}`}
            </span>
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Menyimpan...' : 'Simpan Jurnal'}
            </button>
          </div>
          </>
          )}

          {jurnalMode === 'semi' && (
          <>
          <div className="form-grid" style={{ marginBottom: 16 }}>
            <div className="form-group">
              <label>Tanggal</label>
              <input type="date" value={semiForm.tanggal} onChange={e => setSemiForm({...semiForm, tanggal: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Keterangan</label>
              <input type="text" value={semiForm.keterangan} onChange={e => setSemiForm({...semiForm, keterangan: e.target.value})} placeholder="Contoh: Bayar biaya operasional dari rekening bank" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Kolom IN (Debit) */}
            <div style={{ border: '1px solid #a7f3d0', borderRadius: 10, padding: 12, background: '#f0fdf4' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 800, color: '#065f46', textTransform: 'uppercase' }}>In (Debit)</span>
                <span style={{ fontSize: '0.68rem', color: '#059669' }}>— uang masuk / biaya</span>
              </div>
              {semiForm.ins.map((row, idx) => (
                <div key={`in-${idx}`} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                  <SearchableAkunSelect coaList={coaList} value={row.akun_id}
                    onSelect={id => updateSemi('in', idx, 'akun_id', id)}
                    placeholder="Ketik kode / nama akun..." />
                  <input type="number" value={row.nominal} onChange={e => updateSemi('in', idx, 'nominal', e.target.value)}
                    placeholder="0" style={{ width: 110, padding: '6px 10px', border: '1px solid var(--gray-200)', borderRadius: '6px', fontSize: '0.82rem', textAlign: 'right' }} />
                  <button className="btn-icon btn-danger" onClick={() => removeSemiRow('in', idx)} disabled={semiForm.ins.length <= 1}><X size={13} /></button>
                </div>
              ))}
              <button className="btn btn-secondary" style={{ fontSize: '0.78rem', padding: '5px 10px', marginTop: 2 }} onClick={() => addSemiRow('in')}>
                + Tambah In
              </button>
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #a7f3d0', fontWeight: 700, fontSize: '0.85rem', color: '#065f46' }}>
                Total In: {rupiah(totalIn)}
              </div>
            </div>

            {/* Kolom OUT (Kredit) */}
            <div style={{ border: '1px solid #fca5a5', borderRadius: 10, padding: 12, background: '#fef2f2' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 800, color: '#991b1b', textTransform: 'uppercase' }}>Out (Kredit)</span>
                <span style={{ fontSize: '0.68rem', color: '#dc2626' }}>— sumber dana / keluar</span>
              </div>
              {semiForm.outs.map((row, idx) => (
                <div key={`out-${idx}`} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                  <SearchableAkunSelect coaList={coaList} value={row.akun_id}
                    onSelect={id => updateSemi('out', idx, 'akun_id', id)}
                    placeholder="Ketik kode / nama akun..." />
                  <input type="number" value={row.nominal} onChange={e => updateSemi('out', idx, 'nominal', e.target.value)}
                    placeholder="0" style={{ width: 110, padding: '6px 10px', border: '1px solid var(--gray-200)', borderRadius: '6px', fontSize: '0.82rem', textAlign: 'right' }} />
                  <button className="btn-icon btn-danger" onClick={() => removeSemiRow('out', idx)} disabled={semiForm.outs.length <= 1}><X size={13} /></button>
                </div>
              ))}
              <button className="btn btn-secondary" style={{ fontSize: '0.78rem', padding: '5px 10px', marginTop: 2 }} onClick={() => addSemiRow('out')}>
                + Tambah Out
              </button>
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #fca5a5', fontWeight: 700, fontSize: '0.85rem', color: '#991b1b' }}>
                Total Out: {rupiah(totalOut)}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 12, padding: '8px 12px', background: '#f8f9fe', borderRadius: 'var(--radius)', display: 'flex', justifyContent: 'space-between', fontWeight: 600, flexWrap: 'wrap', gap: 8 }}>
            <span>Total In: <span style={{ color: '#065f46' }}>{rupiah(totalIn)}</span></span>
            <span>Total Out: <span style={{ color: '#991b1b' }}>{rupiah(totalOut)}</span></span>
            <span style={{ color: totalIn === totalOut ? '#065f46' : '#dc2626' }}>
              {totalIn === totalOut ? '✓ Balance' : `Selisih: ${rupiah(totalIn - totalOut)}`}
            </span>
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" onClick={handleSaveSemi} disabled={saving}>
              {saving ? 'Menyimpan...' : 'Simpan Jurnal'}
            </button>
          </div>
          </>
          )}
        </div>
      )}

      {/* ===== Tab Jurnal Otomatis ===== */}
      {activeTab === 'otomatis' && (
        <div>
          <div className="page-header-row" style={{ marginBottom: 12 }}>
            <div className="page-header-actions">
              <button className="btn btn-secondary" onClick={loadKandidat} disabled={autoLoading}>
                <RefreshCw size={14} /> {autoLoading ? 'Memuat...' : 'Refresh'}
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <select value={autoFilter} onChange={e => { setAutoFilter(e.target.value); setAutoMsg(''); }}
                style={{ padding: '6px 10px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius)', fontSize: '0.85rem', background: '#fff' }}>
                <option value="tagihan">Tagihan</option>
                <option value="pembelian">Pembelian Langsung</option>
                <option value="penerimaan">Penerimaan (PO)</option>
                <option value="gaji">Slip Gaji</option>
              </select>
              {autoFilter === 'tagihan' && (
                <select value={autoTipeTagihan} onChange={e => { setAutoTipeTagihan(e.target.value); setAutoMsg(''); }}
                  style={{ padding: '6px 10px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius)', fontSize: '0.85rem', background: '#fff' }}>
                  <option value="tagih">Penagihan (Piutang ↔ Pendapatan)</option>
                  <option value="bayar">Pembayaran (Kas ↔ Piutang)</option>
                </select>
              )}
            </div>
          </div>

          {autoMsg && (
            <div style={{ marginBottom: 12, padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: '0.85rem', fontWeight: 600,
              background: autoMsg.startsWith('✓') ? '#ecfdf5' : '#fef2f2', color: autoMsg.startsWith('✓') ? '#065f46' : '#991b1b' }}>
              {autoMsg}
            </div>
          )}

          {autoLoading ? <div className="page-loading">Memuat kandidat...</div> : (
            <>
              {/* Kartu ringkasan kandidat per sumber */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 16 }}>
                {[
                  { key: 'tagihan', label: 'Tagihan', color: '#1e40af', bg: '#dbeafe', count: ((kandidat?.tagihan?.tagih?.length || 0) + (kandidat?.tagihan?.bayar?.length || 0)) },
                  { key: 'pembelian', label: 'Pembelian', color: '#065f46', bg: '#d1fae5', count: kandidat?.pembelian?.length || 0 },
                  { key: 'penerimaan', label: 'Penerimaan', color: '#5b21b6', bg: '#ede9fe', count: kandidat?.penerimaan?.length || 0 },
                  { key: 'gaji', label: 'Gaji', color: '#9d174d', bg: '#fce7f3', count: kandidat?.gaji?.length || 0 },
                ].map(c => (
                  <div key={c.key} onClick={() => { setAutoFilter(c.key); setAutoMsg(''); }}
                    style={{
                      background: '#fff', borderRadius: 10, cursor: 'pointer', padding: '10px 14px',
                      border: `1.5px solid ${autoFilter === c.key ? c.color : 'var(--gray-100)'}`,
                      borderLeft: `4px solid ${c.color}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', transition: 'all 0.15s',
                    }}>
                    <div style={{ fontSize: '0.68rem', color: c.color, fontWeight: 700, textTransform: 'uppercase' }}>{c.label}</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 800, color: c.color }}>{c.count}</div>
                    <div style={{ fontSize: '0.65rem', color: 'var(--gray-400)' }}>belum berjurnal</div>
                  </div>
                ))}
              </div>

              {/* Daftar kandidat per sumber terpilih */}
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>No. Referensi</th>
                      <th>Deskripsi</th>
                      <th>Tanggal</th>
                      <th>Status</th>
                      <th>Metode</th>
                      <th style={{ textAlign: 'right' }}>Nominal</th>
                      <th style={{ width: 120 }}>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(autoFilter === 'tagihan' ? (kandidat?.tagihan?.[autoTipeTagihan] || []) : (kandidat?.[autoFilter] || [])).map((k, i) => (
                      <tr key={`${autoFilter}-${k.id}-${i}`}>
                        <td><strong style={{ fontSize: '0.8rem' }}>{k.no_ref}</strong></td>
                        <td style={{ fontSize: '0.82rem' }}>{k.deskripsi}</td>
                        <td>{k.tanggal?.slice(0, 10)}</td>
                        <td><span className="badge badge-default" style={{ fontSize: '0.7rem' }}>{k.status}</span></td>
                        <td style={{ fontSize: '0.8rem' }}>{k.metode || '-'}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{rupiah(k.nominal)}</td>
                        <td>
                          <button className="btn btn-primary" style={{ padding: '5px 12px', fontSize: '0.75rem' }}
                            disabled={saving} onClick={() => handleBuatJurnalOtomatis(autoFilter === 'tagihan' ? 'tagihan' : autoFilter, k.id)}>
                            <Zap size={12} /> Buat Jurnal
                          </button>
                        </td>
                      </tr>
                    ))}
                    {(autoFilter === 'tagihan' ? (kandidat?.tagihan?.[autoTipeTagihan] || []) : (kandidat?.[autoFilter] || [])).length === 0 && (
                      <tr><td colSpan={7} className="text-center" style={{ padding: 40, color: 'var(--gray-400)' }}>
                        Tidak ada transaksi {autoFilter === 'tagihan' ? (autoTipeTagihan === 'tagih' ? 'penagihan' : 'pembayaran') : autoFilter} yang belum berjurnal. ✓
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: 10 }}>
                <AlertCircle size={13} style={{ verticalAlign: -2 }} /> Transaksi yang sudah punya jurnal otomatis tidak ditampilkan. Klik <strong>Buat Jurnal</strong> untuk membuat jurnalnya sekarang.
              </p>
            </>
          )}
        </div>
      )}

      {/* Modal Navigasi Silang — detail Penerimaan / PO dari jurnal Penerimaan */}
      {detailRef && (
        <div className="modal-overlay" onClick={() => setDetailRef(null)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 760 }}>
            <div className="modal-header">
              <h3>
                <Eye size={18} style={{ marginRight: 8 }} />
                {detailRef.jenis === 'penerimaan' ? 'Detail Penerimaan' : 'Detail PO Terkait'}
              </h3>
              <button className="btn-icon" onClick={() => setDetailRef(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {detailRef.jenis === 'penerimaan' && detailRef.penerimaan && (
                <>
                  <div style={{ background: '#f5f3ff', borderRadius: 10, padding: '12px 16px', marginBottom: 14, border: '1px solid #ddd6fe', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div><span style={{ fontSize: '0.7rem', color: '#7c3aed' }}>No. Penerimaan</span><div style={{ fontWeight: 700 }}>{detailRef.penerimaan.no_penerimaan}</div></div>
                    <div><span style={{ fontSize: '0.7rem', color: '#7c3aed' }}>No. PO</span><div style={{ fontWeight: 700 }}>{detailRef.penerimaan.no_po || '-'}</div></div>
                    <div><span style={{ fontSize: '0.7rem', color: '#7c3aed' }}>Supplier</span><div style={{ fontWeight: 600 }}>{detailRef.penerimaan.supplier_name || '-'}</div></div>
                    <div><span style={{ fontSize: '0.7rem', color: '#7c3aed' }}>Tanggal</span><div style={{ fontWeight: 600 }}>{detailRef.penerimaan.tanggal || '-'}</div></div>
                    <div><span style={{ fontSize: '0.7rem', color: '#7c3aed' }}>Total</span><div style={{ fontWeight: 800, color: '#5b21b6' }}>{rupiah(detailRef.penerimaan.total_amount)}</div></div>
                    <div><span style={{ fontSize: '0.7rem', color: '#7c3aed' }}>Metode Bayar</span>
                      <div>{detailRef.penerimaan.metode_pembayaran
                        ? <span className="badge" style={{ background: '#ede9fe', color: '#5b21b6' }}>{detailRef.penerimaan.metode_pembayaran}</span>
                        : <span style={{ fontSize: '0.75rem', color: '#b45309' }}>Belum dipilih</span>}
                      </div>
                    </div>
                  </div>
                  <div className="table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr><th>No</th><th>Kode</th><th>Nama Sparepart</th><th>Dipesan</th><th>Diterima</th><th>Harga Satuan</th><th>Subtotal</th></tr>
                      </thead>
                      <tbody>
                        {(detailRef.penerimaan.items || []).map((it, i) => (
                          <tr key={i}>
                            <td>{i + 1}</td>
                            <td>{it.code || '-'}</td>
                            <td>{it.name || '-'}</td>
                            <td>{it.qty_dipesan || 0}</td>
                            <td style={{ fontWeight: 600, color: '#059669' }}>{it.qty_diterima}</td>
                            <td>{rupiah(it.harga_satuan)}</td>
                            <td style={{ fontWeight: 600 }}>{rupiah(it.subtotal)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {detailRef.jenis === 'po' && detailRef.po && (
                <>
                  <div style={{ background: '#f0f4ff', borderRadius: 10, padding: '12px 16px', marginBottom: 14, border: '1px solid #d6e4ff', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div><span style={{ fontSize: '0.7rem', color: '#4f6ef7' }}>No. PO</span><div style={{ fontWeight: 700 }}>{detailRef.po.no_po}</div></div>
                    <div><span style={{ fontSize: '0.7rem', color: '#4f6ef7' }}>Tanggal</span><div style={{ fontWeight: 600 }}>{detailRef.po.tanggal || '-'}</div></div>
                    <div><span style={{ fontSize: '0.7rem', color: '#4f6ef7' }}>Supplier</span><div style={{ fontWeight: 600 }}>{detailRef.po.supplier_name || '-'}</div></div>
                    <div><span style={{ fontSize: '0.7rem', color: '#4f6ef7' }}>Gudang</span><div style={{ fontWeight: 600 }}>{detailRef.po.gudang_nama || '-'}</div></div>
                    <div><span style={{ fontSize: '0.7rem', color: '#4f6ef7' }}>Total</span><div style={{ fontWeight: 800, color: '#1e3a8a' }}>{rupiah(detailRef.po.total_amount)}</div></div>
                    <div><span style={{ fontSize: '0.7rem', color: '#4f6ef7' }}>Status</span>
                      <div><span className={`badge ${detailRef.po.status === 'Selesai' ? 'badge-success' : detailRef.po.status === 'Diverifikasi' ? 'badge-warning' : 'badge-default'}`}>{detailRef.po.status}</span></div>
                    </div>
                  </div>
                  <div className="table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr><th>No</th><th>Kode</th><th>Nama Sparepart</th><th>Qty</th><th>Harga Satuan</th><th>Subtotal</th></tr>
                      </thead>
                      <tbody>
                        {(detailRef.po.items || []).map((it, i) => (
                          <tr key={i}>
                            <td>{i + 1}</td>
                            <td>{it.code || '-'}</td>
                            <td>{it.name || '-'}</td>
                            <td>{it.qty}</td>
                            <td>{rupiah(it.harga_satuan)}</td>
                            <td style={{ fontWeight: 600 }}>{rupiah(it.subtotal)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              {detailRef.jenis === 'penerimaan' && detailRef.penerimaan?.po_id && (
                <button className="btn btn-secondary" onClick={openPODariPenerimaan}>
                  <RotateCcw size={16} /> Lihat PO Terkait
                </button>
              )}
              {detailRef.jenis === 'po' && (
                <button className="btn btn-secondary" onClick={() => setDetailRef({ ...detailRef, jenis: 'penerimaan' })}>
                  <RotateCcw size={16} /> Kembali ke Penerimaan
                </button>
              )}
              <button className="btn btn-primary"
                onClick={() => { setDetailRef(null); navigate('/inventori/purchase-order'); }}>
                <ArrowRight size={16} /> Buka di Purchase Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
